<template>
  <div class="exam-examlist" v-loading="loading">
    <el-tabs v-model="activeTab" class="exam-tabs">
      <!-- 可报名考试 -->
      <el-tab-pane label="可报名考试" name="canSignUp">
        <div v-if="canSignUpList.length" class="exam-grid">
          <div v-for="item in canSignUpList" :key="item.id" class="exam-card">
            <div class="card-head">
              <span class="exam-name">{{ item.name }}</span>
              <el-tag size="small" type="success">报名中</el-tag>
            </div>
            <div class="card-body">
              <div class="row">
                <span class="label">报名时间</span>
                <span class="value">{{ formatDate(item.signStartDate) }} ~ {{ formatDate(item.signEndDate) }}</span>
              </div>
            </div>
            <div class="card-foot">
              <el-button type="primary" size="small" @click="goSignUp(item)">去报名</el-button>
            </div>
          </div>
        </div>
        <el-empty v-else description="暂无可以报名的考试" />
      </el-tab-pane>

      <!-- 已报名考试 -->
      <el-tab-pane label="已报名考试" name="registered">
        <div v-if="registeredList.length" class="registered-list">
          <div v-for="item in registeredList" :key="item.registrationId" class="registered-item"
            @click="viewExam(item)">
            <div class="item-main">
              <div class="item-title">
                <span class="exam-name">{{ item.examName }}</span>
                <el-tag size="small" :type="auditTagType(item)">{{ auditStatusText(item) }}</el-tag>
              </div>
              <div class="item-info">
                <span class="info-cell">
                  <span class="label">报名时间：</span>{{ formatDate(item.signStartDate) }} ~ {{ formatDate(item.signEndDate) }}
                </span>
              </div>
            </div>
            <div class="item-foot">
              <span class="submit-time">提交时间：{{ formatDateTime(item.submitTime) }}</span>
              <span class="view-more" v-if="item.examId">查看报名信息 <i class="el-icon-arrow-right"></i></span>
            </div>
          </div>
        </div>
        <el-empty v-else description="您还未报名任何考试" />
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import * as ExamApi from "@/api/exam/index";
import { mapMutations, mapActions } from "vuex";
// 报名审核状态统一映射（与后台字典口径一致）
import { getAuditStatusText, getAuditStatusTagType } from "@/utils/auditStatus";

export default {
  name: "ExamExamList",
  data() {
    return {
      loading: false,
      activeTab: "canSignUp",
      canSignUpList: [],
      registeredList: [],
    };
  },
  created() {
    this.getExamList();
  },
  methods: {
    ...mapMutations(["SET_CURRENT_EXAM_ID"]),
    ...mapActions(["fetchExamInfoById", "fetchExamDataList"]),
    async getExamList() {
      this.loading = true;
      try {
        const res = await ExamApi.getAppExamList();
        this.canSignUpList = res?.canSignUpList || [];
        this.registeredList = res?.registeredList || [];
      } catch (e) {
        this.$message.error("考试列表加载失败");
      } finally {
        this.loading = false;
      }
    },
    // 兼容后端返回的毫秒时间戳或 ISO 字符串
    toDate(v) {
      if (v === null || v === undefined || v === "") return null;
      // 数字时间戳（毫秒）
      if (typeof v === "number") return new Date(v);
      // 纯数字字符串时间戳
      if (/^\d{10,13}$/.test(String(v))) return new Date(Number(v));
      // ISO 字符串（可能带 T 或空格）
      return new Date(String(v).replace(" ", "T"));
    },
    formatDate(v) {
      const d = this.toDate(v);
      if (!d || isNaN(d.getTime())) return "-";
      const pad = (n) => (n < 10 ? "0" + n : n);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    formatDateTime(v) {
      const d = this.toDate(v);
      if (!d || isNaN(d.getTime())) return "-";
      const pad = (n) => (n < 10 ? "0" + n : n);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
    // 审核状态文案：统一走报名状态枚举映射（见 @/utils/auditStatus）
    auditStatusText(item) {
      return getAuditStatusText(item.auditStatus, item.submitStatus);
    },
    auditTagType(item) {
      return getAuditStatusTagType(item.auditStatus, item.submitStatus);
    },
    // 去报名：切换当前考试并进入报名流程
    async goSignUp(item) {
      try {
        this.SET_CURRENT_EXAM_ID(item.id);
        await this.fetchExamInfoById();
        this.$router.push({ name: "exam-notice" });
      } catch (e) {
        this.$message.error("考试信息加载失败，请重试");
      }
    },
    // 查看已报名考试的报名信息：切换当前考试并进入考生报名页
    viewExam(item) {
      this.SET_CURRENT_EXAM_ID(item.examId);
      this.fetchExamInfoById().finally(() => {
        this.$router.push({ name: "exam-sign-up" });
      });
    },
  },
};
</script>

<style scoped lang="scss">
.exam-examlist {
  padding: 20px 24px;

  .exam-tabs {
    :deep(.el-tabs__item) {
      font-size: 16px;
    }
  }

  .exam-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(480px, 1fr));
    gap: 20px;
    margin-top: 12px;
  }

  .exam-card {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 180px;
    transition: box-shadow 0.2s;

    &:hover {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    }

    .card-head {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .exam-name {
      font-size: 18px;
      font-weight: bold;
      color: #303133;
    }

    .card-body {
      flex: 1;

      .row {
        display: flex;
        margin-bottom: 6px;
        font-size: 14px;

        .label {
          width: 80px;
          color: #909399;
          flex-shrink: 0;
        }

        .value {
          color: #606266;
        }
      }
    }

    .card-foot {
      display: flex;
      justify-content: flex-end;
    }
  }

  .registered-list {
    margin-top: 12px;

    .registered-item {
      border: 1px solid #ebeef5;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 12px;
      cursor: pointer;
      transition: box-shadow 0.2s, border-color 0.2s;

      &:hover {
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
        border-color: #409eff;
      }

      .item-title {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 10px;

        .exam-name {
          font-size: 16px;
          font-weight: bold;
          color: #303133;
        }
      }

      .item-info {
        display: flex;
        flex-wrap: wrap;
        gap: 24px;
        margin-bottom: 6px;
        font-size: 13px;

        .label {
          color: #909399;
        }
      }

      .item-foot {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 10px;

        .submit-time {
          font-size: 12px;
          color: #c0c4cc;
        }

        .view-more {
          font-size: 13px;
          color: #409eff;
        }
      }
    }
  }
}

/* 手机端适配 */
@media screen and (max-width: 768px) {
  .exam-examlist {
    padding: 12px;
  }

  .exam-grid {
    grid-template-columns: 1fr;
  }
}
</style>
